import { ForReviewCommonFilter } from '../../../features/tasks/types/forReviewFilter';
import { TaskTypes } from '../../../features/tasks/types/taskTypes';
import { IFilter } from '../../interfaces/IFilter';

/**
 * Builds a URL for fetching applications with the specified parameters and filters
 * @param params - Configuration object for building the URL
 * @param params.viewName - The name of the view to fetch
 * @param params.pageNumber - The page number for pagination
 * @param params.prioritizedView - Whether to show prioritized view
 * @param params.limit - Number of items per page (default: 10)
 * @param params.sortBy - Field to sort by
 * @param params.sortOrder - Sort direction ('ASC' or 'DESC')
 * @param params.decisionFilter - Array of decision filters
 * @param params.stageFilter - Array of stage filters
 * @param params.lifecycleFilters - Array of lifecycle filters
 * @param params.detailedLifecycleFilters - Array of detailed lifecycle filters
 * @param params.forReviewFilter - Array of review status filters
 * @param params.productFilter - Array of product filters
 * @param params.userFilter - Array of user filters
 * @param params.carrierUserFilter - Array of carrier user filters
 * @param params.searchFilter - Search query string
 * @param params.fromDate - Start date filter
 * @param params.toDate - End date filter
 * @returns A URL string with all the specified parameters and filters
 */
export const buildApplicationsFetchUrl = (params: {
  viewName: string;
  pageNumber: number;
  prioritizedView: boolean;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  decisionFilter?: IFilter[];
  stageFilter?: IFilter[];
  lifecycleFilters?: IFilter[];
  detailedLifecycleFilters?: IFilter[];
  forReviewFilter?: IFilter[];
  productFilter?: IFilter[];
  userFilter?: IFilter[];
  carrierUserFilter?: IFilter[];
  searchFilter?: string;
  agencySearchFilter?: string;
  fromDate?: string;
  toDate?: string;
}) => {
  const limit = params.limit || 10;
  let path = `${params.viewName}?limit=${limit}&page=${params.pageNumber}&prioritized_view=${params.prioritizedView}`;

  // Add sort parameters if provided
  if (params.sortBy) {
    // needed because FE column name is different from BE column name
    if (params.sortBy === 'creationDate') {
      path = `${path}&sort_by=createdAt&sort_order=${
        params.sortOrder || 'DESC'
      }`;
    } else {
      path = `${path}&sort_by=${params.sortBy}&sort_order=${
        params.sortOrder || 'DESC'
      }`;
    }
  }

  // Helper function to build filter strings
  const buildFilterString = (items: IFilter[], filterName: string) => {
    if (!items?.length) return '';
    return items.reduce((prevVal: string, currVal: any, idx: number) => {
      return idx === 0
        ? `${filterName}[]=${currVal.value}`
        : prevVal + `&${filterName}[]=${currVal.value}`;
    }, '');
  };

  // Add all filters
  const filters: { data: IFilter[]; name: string }[] = [
    { data: params.decisionFilter || [], name: 'filter_decision' },
    { data: params.stageFilter || [], name: 'filter_stage' },
    { data: params.lifecycleFilters || [], name: 'lifecycleFilter' },
    {
      data: params.detailedLifecycleFilters || [],
      name: 'filter_detailed_lifecycle',
    },
    { data: params.productFilter || [], name: 'product' },
    { data: params.userFilter || [], name: 'filter_user' },
    { data: params.carrierUserFilter || [], name: 'filterCarrierUser' },
  ];

  filters.forEach(filter => {
    const filterString = buildFilterString(filter.data || [], filter.name);
    if (filterString) {
      path = `${path}&${filterString}`;
    }
  });

  // Special handling for forReviewFilter
  if (params.forReviewFilter && params.forReviewFilter.length) {
    const values: string[] = [];
    for (const filterItem of params.forReviewFilter) {
      if (ForReviewCommonFilter.has(filterItem.value as TaskTypes)) {
        values.push(
          ...(ForReviewCommonFilter.get(filterItem.value as TaskTypes) as []),
        );
      } else {
        values.push(filterItem.value);
      }
    }
    const filter = values.reduce(
      (prevVal: string, currVal: any, idx: number) => {
        return idx === 0
          ? `for_review[]=${currVal}`
          : prevVal + `&for_review[]=${currVal}`;
      },
      '',
    );
    path = `${path}&${filter}`;
  }

  // Add search and date filters
  if (params.searchFilter) {
    path = `${path}&search=${encodeURIComponent(params.searchFilter)}`;
  }
  if (params.agencySearchFilter) {
    path = `${path}&agencySearch=${encodeURIComponent(
      params.agencySearchFilter,
    )}`;
  }
  if (params.fromDate) {
    path = `${path}&from_date=${params.fromDate}`;
  }
  if (params.toDate) {
    path = `${path}&to_date=${params.toDate}`;
  }

  return path;
};
